import type { PostStationDomainOrigin } from '@/Network/Types/Responses/PostStationResponses'

// Os VALORES são os do contrato da API (`PostStationConsolidatedStatus`); os NOMES
// são os históricos da UI — STATUS_CONFIG/STATUS_FILTER_OPTIONS chaveiam por membro.
export enum PostStationStatus {
  APPROVED = 'APPROVED',
  REVIEW = 'REVIEW',
  IN_EVALUATION = 'UNDER_VALIDATION',
  IN_ANALYSIS = 'UNDER_ANALYSIS',
  REVERTED = 'REVERTED'
}

/** `not_required` (Q1): neutro — nem conforme nem não conforme; não conta como problema. */
export type ComplianceState = 'ok' | 'fail' | 'pending' | 'not_required'

/**
 * F4 — os QUATRO estados da coluna "Reg. Plantão" (M5, balão ①). São os do
 * `EffectiveRegistrationStatus` da F1; o quinto rótulo da tela, "Não avaliado",
 * NÃO é um estado do registro: é o domínio sem política vigente, que chega como
 * `ComplianceState = 'pending'` e vale para qualquer valor de registro.
 */
export type RegistrationStatusValue = 'OK' | 'PARTIAL' | 'PENDING' | 'NONE' | 'NOT_REQUIRED'

// Não importar o type do SFC LabelBadge.vue: o build webpack (ts-loader) só
// enxerga o export default dos .vue via shim — importar type nomeado quebra o build.
export type PostStationStatusColor = 'green' | 'orange' | 'blue' | 'yellow' | 'gray'

export interface PostStationDoctor {
  name: string
  crm: string
  avatarUrl?: string | null
  npsScore?: number | null
}

export interface PostStationCompliance {
  registration: ComplianceState
  qualification: ComplianceState
  repass: ComplianceState
  exception: ComplianceState
  payment: ComplianceState
}

export interface PostStationCriterion {
  key: string
  label: string
  description?: string
  passed: boolean
  actual?: string
  /**
   * F4 (T7) — natureza e rótulo da FONTE VENCEDORA da perna, quando o critério
   * é `checkin`/`checkout` do domínio de registro. Opcionais: os outros quatro
   * avaliadores não emitem esses campos, e um backend anterior à F4 não os manda.
   */
  sourceKind?: string | null
  sourceLabel?: string | null
}

export interface PostStationDomainDrilldown {
  doctor: PostStationDoctor
  // Nullable: a sidebar cai no valor da row quando a API envia null.
  formation: string | null
  team: string | null
  position: string | null
  shift: string | null
  company: string
  unit: string
  schedule: string
  state: ComplianceState
  criteria: PostStationCriterion[]
  origin: PostStationDomainOrigin
  /**
   * F4 (T8) — o gate configurável por escala ("Exigir Registro de Plantão
   * completo"). `false` ⇒ o domínio é informativo e nunca impede a aprovação.
   *
   * `undefined` = backend anterior à F4: mantém o comportamento de hoje, que é
   * BLOQUEANTE. Afrouxar por omissão anunciaria "informativo" um domínio que na
   * prática segura o plantão.
   */
  registrationBlocking?: boolean
}

export interface PostStationDomainDetail {
  key: keyof PostStationCompliance
  label: string
  state: ComplianceState
  criteria: PostStationCriterion[]
}

export interface PostStationRowDetail {
  formation: string
  team: string
  position: string
  shift: string
  dutyLabel: string
  company: string
  unit: string
  schedule: string
  domains: PostStationDomainDetail[]
}

export interface PostStationRow {
  id: number
  evaluationId: number
  doctorStationId: number
  stationId: number
  scheduleId: number
  doctor: PostStationDoctor
  unit: string
  scheduleLabel: string
  date: string
  dateIso: string
  timeRange: string
  compliance: PostStationCompliance
  status: PostStationStatus
  detail: PostStationRowDetail
  /**
   * F4 — estado do horário efetivo do plantão (`EffectiveRegistrationStatus`),
   * servido pelo grid do backend (T9). Opcional porque um backend anterior não
   * o manda, e a coluna cai no estado de conformidade de hoje em vez de mostrar
   * "Sem registro" para um plantão que tem registro.
   *
   * `string` e não o union: um valor novo do backend tem de chegar à tela como
   * texto cru, nunca sumir da linha.
   */
  effectiveRegistrationStatus?: string | null
  /**
   * F4 (T9) — havia política de registro VIGENTE na data do plantão? `false` é
   * o que separa "Não avaliado" de "Sem registro": sem política ninguém avaliou,
   * e pintar a bolinha de "Sem registro" seria acusação indevida (balão ⑤).
   *
   * `null`/`undefined` = backend anterior à T9 ⇒ a coluna cai no estado de
   * conformidade de hoje.
   */
  registrationPolicyInEffect?: boolean | null
}
